import { formatLbp, formatMoment, formatUsd } from '@/lib/money'
import { msg, t } from '@/i18n'

export interface DrawerStatementEntry {
  occurredAtUtc: string
  what: string
  usd: number
  lbp: number
}

export interface DrawerStatementLine {
  label: string
  usd: number
  lbp: number
  runningUsd: number
  runningLbp: number
  entries: DrawerStatementEntry[]
}

export interface DrawerStatementData {
  lines: DrawerStatementLine[]
  expectedUsd: number
  expectedLbp: number
}

/** A signed figure, with the sign carried by the colour as well as the minus. */
function Signed({ value, format }: { value: number; format: (amount: number) => string }) {
  if (value === 0) {
    return <span className="text-[var(--text-secondary)]">—</span>
  }

  return (
    <span className={value < 0 ? 'text-danger-500' : 'text-success-500'}>
      {value > 0 ? '+' : ''}
      {format(value)}
    </span>
  )
}

/**
 * How the drawer got from empty to what it should hold.
 *
 * Every step in order with the balance after it, in both currencies side by side — because the
 * question a cashier is actually asking at close is "why does it say this?", and a bare expected
 * figure cannot answer it. The individual movements sit under the line they belong to: nobody
 * queries the takings, they query the pay-out nobody remembers making.
 *
 * The running balances come from the server, computed off the same components as the expected
 * figure, so the statement cannot explain a total different from the one being counted against.
 */
export function DrawerStatement({
  statement,
  title = msg('How the drawer got here'),
}: {
  statement: DrawerStatementData
  title?: string
}) {
  return (
    <section className="surface-raised rounded-[var(--radius-card)] border">
      <div className="border-b border-[var(--border-subtle)] px-4 py-3">
        <p className="text-sm font-semibold">{t(title)}</p>
        <p className="text-xs text-[var(--text-secondary)]">
          {t('Every step from an empty till to now. Dollars and lira are separate piles and never cover each other.')}
        </p>
      </div>

      {/* Scrolls inside itself so a wide statement never makes the page scroll sideways. */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border-subtle)] text-xs text-[var(--text-secondary)]">
              <th className="px-4 py-2 text-start font-medium">{t('Step')}</th>
              <th className="px-3 py-2 text-end font-medium">{t('Dollars')}</th>
              <th className="px-3 py-2 text-end font-medium">{t('Lira')}</th>
              <th className="px-4 py-2 text-end font-medium">{t('In the drawer')}</th>
            </tr>
          </thead>

          <tbody>
            {statement.lines.map((line) => (
              <tr key={line.label} className="border-b border-[var(--border-subtle)] align-top">
                <td className="px-4 py-2">
                  <span className="font-medium">{t(line.label)}</span>

                  {line.entries.length > 0 && (
                    <ul className="mt-1 flex flex-col gap-0.5">
                      {line.entries.map((entry, index) => (
                        <li key={index} className="text-xs text-[var(--text-secondary)]">
                          <span className="tabular">{formatMoment(entry.occurredAtUtc)}</span> ·{' '}
                          {entry.what}
                          {entry.usd !== 0 && <> · {formatUsd(entry.usd)}</>}
                          {entry.lbp !== 0 && <> · {formatLbp(entry.lbp)}</>}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>

                <td className="tabular whitespace-nowrap px-3 py-2 text-end">
                  <Signed value={line.usd} format={formatUsd} />
                </td>
                <td className="tabular whitespace-nowrap px-3 py-2 text-end">
                  <Signed value={line.lbp} format={formatLbp} />
                </td>
                <td className="tabular whitespace-nowrap px-4 py-2 text-end">
                  {formatUsd(line.runningUsd)}
                  <span className="block text-xs text-[var(--text-secondary)]">
                    {formatLbp(line.runningLbp)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr className="font-semibold">
              <td className="px-4 py-3" colSpan={3}>
                {t('Should be in the drawer now')}
              </td>
              <td className="tabular whitespace-nowrap px-4 py-3 text-right">
                {formatUsd(statement.expectedUsd)}
                <span className="block text-xs font-normal text-[var(--text-secondary)]">
                  {formatLbp(statement.expectedLbp)}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  )
}
